// src/components/Hero/Hero.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo?.name || "Antivoza";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    website: "",
    budget: "",
    services: { web: false, marketing: false },
    requirements: "",
  });

  const [status, setStatus] = useState({ loading: false, ok: "", err: "" });

  const headline = useMemo(
    () => ({
      top: "Digital strategy & execution",
      main: "Grow your business with data-driven marketing.",
      sub: `${brandName} delivers strategy, paid ads, SEO, landing pages, and automation—so you get more leads and revenue without the guesswork.`,
    }),
    [brandName]
  );

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    setStatus({ loading: false, ok: "", err: "" });

    if (name === "web" || name === "marketing") {
      setForm((p) => ({
        ...p,
        services: { ...p.services, [name]: checked },
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return setStatus({ loading: false, ok: "", err: "Please enter your name." });
    if (!form.phone.trim()) return setStatus({ loading: false, ok: "", err: "Please enter your phone number." });

    try {
      setStatus({ loading: true, ok: "", err: "" });

      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        budget: form.budget.trim(),
        services: Object.keys(form.services).filter((k) => form.services[k]),
        requirements: form.requirements.trim(),
        source: "hero_lead_form",
      };

      if (typeof onSubmitLead === "function") {
        await onSubmitLead(payload);
      }

      setStatus({ loading: false, ok: "Thanks! We’ll reach out shortly.", err: "" });
      setForm({
        name: "",
        phone: "",
        website: "",
        budget: "",
        services: { web: false, marketing: false },
        requirements: "",
      });
    } catch (err) {
      setStatus({ loading: false, ok: "", err: "Something went wrong. Please try again." });
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
  };

  return (
    <section className="hero-digi" aria-label={`${brandName} hero`}>
      <div className="hero-digi-bg" aria-hidden="true" />
      <div className="hero-digi-shell">
        <motion.div className="hero-digi-grid" initial="hidden" animate="show" variants={container}>
          <motion.div className="hero-digi-left" variants={fadeUp}>
            <div className="hero-digi-badge">
              <span className="hero-digi-dot" aria-hidden="true" />
              <span>Strategy • Ads • SEO • Funnels • Automation</span>
            </div>

            <h1 className="hero-digi-title">
              <span className="hero-digi-title-top">{headline.top}</span>
              <span className="hero-digi-title-main">{headline.main}</span>
            </h1>

            <p className="hero-digi-sub">{headline.sub}</p>

            <div className="hero-digi-points" aria-label="What we do">
              <div className="hero-digi-point">
                <span className="hero-digi-point-k">Full funnel</span>
                <span className="hero-digi-point-v">Ads → Landing pages → CRM → Retention</span>
              </div>
              <div className="hero-digi-point">
                <span className="hero-digi-point-k">Tracking</span>
                <span className="hero-digi-point-v">Pixels, events, dashboards & clear reporting</span>
              </div>
              <div className="hero-digi-point">
                <span className="hero-digi-point-k">Execution</span>
                <span className="hero-digi-point-v">Weekly optimization, fast iterations</span>
              </div>
            </div>

            <div className="hero-digi-trust">
              <span className="hero-digi-pill">Transparent reporting</span>
              <span className="hero-digi-pill">No long-term lock-in</span>
              <span className="hero-digi-pill">Global clients</span>
            </div>
          </motion.div>

          <motion.aside className="hero-digi-card" variants={fadeUp} aria-label="Get in touch">
            <h2 className="hero-digi-form-title">Start a conversation</h2>

            <form className="hero-digi-form" onSubmit={handleSubmit}>
              <label className="hero-digi-field">
                <span className="sr-only">Name</span>
                <input name="name" value={form.name} onChange={onChange} placeholder="Your name" autoComplete="name" />
              </label>
              <label className="hero-digi-field">
                <span className="sr-only">Phone</span>
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone number" autoComplete="tel" />
              </label>
              <label className="hero-digi-field">
                <span className="sr-only">Business or website</span>
                <input name="website" value={form.website} onChange={onChange} placeholder="Business or website" autoComplete="url" />
              </label>
              <label className="hero-digi-field">
                <span className="sr-only">Budget</span>
                <input name="budget" value={form.budget} onChange={onChange} placeholder="Budget (optional)" />
              </label>

              <div className="hero-digi-services">
                <span className="hero-digi-services-label">I need</span>
                <label className="hero-digi-check">
                  <input type="checkbox" name="web" checked={form.services.web} onChange={onChange} />
                  <span>Web & landing pages</span>
                </label>
                <label className="hero-digi-check">
                  <input type="checkbox" name="marketing" checked={form.services.marketing} onChange={onChange} />
                  <span>Digital marketing</span>
                </label>
              </div>

              <label className="hero-digi-field hero-digi-field--area">
                <span className="sr-only">Requirements</span>
                <textarea name="requirements" value={form.requirements} onChange={onChange} placeholder="Tell us about your goals (optional)" rows={4} />
              </label>

              {status.err ? <div className="hero-digi-msg hero-digi-msg--err">{status.err}</div> : null}
              {status.ok ? <div className="hero-digi-msg hero-digi-msg--ok">{status.ok}</div> : null}

              <button className="hero-digi-submit" type="submit" disabled={status.loading}>
                {status.loading ? "Sending…" : "Send message"}
              </button>

              <p className="hero-digi-note">By submitting you agree to be contacted about your request.</p>
            </form>
          </motion.aside>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
